import { useState } from 'react';
import { createPortal } from 'react-dom';
import { RiArrowDownSLine, RiCloseLine } from '@remixicon/react';
import { COUNTRY_CODES, FlagImg } from '@/utils/countryCode';

/**
 * PhoneInput Component
 * Phone number input with country code picker (popup modal).
 *
 * @param {Object} props
 * @param {string} props.countryCode - Selected country code (e.g. "+1")
 * @param {string} props.phone - Phone number digits (no country code)
 * @param {Function} props.onCountryCodeChange - Callback (code: string) => void
 * @param {Function} props.onPhoneChange - Callback (phone: string) => void
 * @param {string} [props.label="Phone Number"] - Field label
 * @param {boolean} [props.required=false] - Whether to show the required asterisk
 * @param {string} [props.error] - Error message to display
 * @param {string} [props.placeholder="(555) 000-0000"] - Input placeholder
 * @param {number} [props.maxLength=10] - Max number of digits
 */
const PhoneInput = ({
  countryCode,
  phone,
  onCountryCodeChange,
  onPhoneChange,
  label = 'Phone Number',
  required = false,
  error,
  placeholder = '(555) 000-0000',
  maxLength = 10,
  showCountryCode = true,
}) => {
  const [open, setOpen] = useState(false);

  const selectedIso = COUNTRY_CODES.find((c) => c.code === countryCode)?.iso || 'us';

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative flex items-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="absolute left-3 flex items-center gap-1.5 z-10 cursor-pointer"
        >
          <FlagImg iso={selectedIso} className="w-6 h-6" />
          {showCountryCode && (
            <span className="text-sm text-gray-700 font-medium">{countryCode}</span>
          )}
          <RiArrowDownSLine className="w-3.5 h-3.5 text-gray-400" />
        </button>
        <input
          type="tel"
          value={phone}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, maxLength);
            onPhoneChange(digits);
          }}
          placeholder={placeholder}
          className={`w-full ${showCountryCode ? 'pl-[75px]' : 'pl-[58px]'} pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
            error ? 'border-red-500' : 'border-gray-200'
          }`}
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}

      {/* Country code popup via portal */}
      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998] bg-black/30" onClick={() => setOpen(false)} />
          <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-base font-semibold text-primary-700">Country Code</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="pb-3 max-h-72 overflow-y-auto">
              {COUNTRY_CODES.map((c) => (
                <button
                  key={c.code + c.iso}
                  type="button"
                  onClick={() => {
                    onCountryCodeChange(c.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors hover:bg-gray-50 ${
                    countryCode === c.code ? 'text-primary-600 font-medium' : 'text-gray-700'
                  }`}
                >
                  <FlagImg iso={c.iso} className="w-6 h-6" />
                  <span>{c.label}</span>
                  <span className="text-gray-400">{c.code}</span>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default PhoneInput;
